import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Edit2, Plus, Check, Trash2, Trophy, ExternalLink } from 'lucide-react';
import { playerApi } from '../../utils/api';
import { supabase } from '../../lib/supabase';
import { playerCardService } from '../../services/playerCardService';
import { playerGoalService } from '../../services/playerGoalService';
import { gameService, gameResult } from '../../services/gameService';
import { getPositionGroupsForSport } from '../../data/positionHierarchy';
import { getStatCardProfile } from '../../data/positionStatCards';
import { getEventLabel } from '../../data/sportRulesets';
import { getCalculatedStats, formatCalculatedStat } from '../../utils/statCalculations';
import type { Player, PlayerCard, PlayerGoal, Team } from '../../types';

interface IndividualProfileCardProps {
  team: Team;
}

const GOAL_CATEGORIES: PlayerGoal['category'][] = ['skill', 'physical', 'mental', 'team'];

/**
 * The individual (parent/player) persona's home-page profile. Removing the
 * Roster page for personal accounts (sc-336) also removed their only path to
 * a player card, goals, and stats — this puts a lightweight version of that
 * right on the Dashboard, with a link out to the full player-card page
 * (Overview/Goals/Notes — Attendance is hidden there too, same reasoning)
 * for anything deeper.
 */
export const IndividualProfileCard: React.FC<IndividualProfileCardProps> = ({ team }) => {
  const navigate = useNavigate();
  const [player, setPlayer] = useState<Player | null>(null);
  const [card, setCard] = useState<PlayerCard | null>(null);
  const [goals, setGoals] = useState<PlayerGoal[]>([]);
  const [record, setRecord] = useState<{ w: number; l: number; t: number }>({ w: 0, l: 0, t: 0 });
  const [loading, setLoading] = useState(true);

  const [editingBio, setEditingBio] = useState(false);
  const [nicknameDraft, setNicknameDraft] = useState('');
  const [bioDraft, setBioDraft] = useState('');
  const [positionDraft, setPositionDraft] = useState('');
  const [savingBio, setSavingBio] = useState(false);

  const [rawStatTotals, setRawStatTotals] = useState<Array<{ stat_key: string; stat_value: number }>>([]);

  const [newGoalText, setNewGoalText] = useState('');
  const [newGoalCategory, setNewGoalCategory] = useState<PlayerGoal['category']>('skill');
  const [addingGoal, setAddingGoal] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const playersResult = await playerApi.getByTeam(team.id);
      const p = playersResult.success && playersResult.data && playersResult.data.length > 0 ? playersResult.data[0] : null;
      if (cancelled) return;
      setPlayer(p);
      if (!p) {
        setLoading(false);
        return;
      }

      try {
        const [cardResult, gamesResult, statTotalsResult] = await Promise.all([
          playerCardService.getOrCreatePlayerCard(p.id, team.id),
          gameService.getGames(team.id),
          supabase.rpc('get_team_stat_totals', { p_team_id: team.id }),
        ]);
        if (cancelled) return;

        setCard(cardResult);
        setNicknameDraft(cardResult.nickname || '');
        setBioDraft(cardResult.bio || '');
        setPositionDraft(p.position || '');

        let w = 0, l = 0, t = 0;
        for (const g of gamesResult) {
          const r = gameResult(g);
          if (r === 'W') w++;
          else if (r === 'L') l++;
          else if (r === 'T') t++;
        }
        setRecord({ w, l, t });

        const statRows = (statTotalsResult.data || []) as Array<{ player_card_id: string; stat_key: string; total_value: number }>;
        setRawStatTotals(
          statRows
            .filter((row) => row.player_card_id === cardResult.id)
            .map((row) => ({ stat_key: row.stat_key, stat_value: Number(row.total_value) }))
        );

        const goalsList = await playerGoalService.getGoals(cardResult.id);
        if (cancelled) return;
        setGoals(goalsList);
      } catch (error) {
        console.error('[IndividualProfileCard] Error loading profile:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [team.id]);

  const handleSaveBio = async () => {
    if (!card || !player) return;
    setSavingBio(true);
    try {
      const updated = await playerCardService.updatePlayerCard(card.id, {
        nickname: nicknameDraft.trim() || undefined,
        bio: bioDraft.trim() || undefined,
      });
      setCard(updated);

      if (positionDraft !== (player.position || '')) {
        const { error } = await supabase.from('players').update({ position: positionDraft }).eq('id', player.id);
        if (error) throw error;
        setPlayer({ ...player, position: positionDraft });
      }

      setEditingBio(false);
      toast.success('Profile updated');
    } catch (error) {
      console.error('[IndividualProfileCard] Error saving bio:', error);
      toast.error('Failed to save profile');
    } finally {
      setSavingBio(false);
    }
  };

  const handleAddGoal = async () => {
    if (!card || !newGoalText.trim()) return;
    setAddingGoal(true);
    try {
      const goal = await playerGoalService.createGoal(card.id, {
        goal_text: newGoalText.trim(),
        category: newGoalCategory,
      });
      setGoals((prev) => [goal, ...prev]);
      setNewGoalText('');
    } catch (error) {
      console.error('[IndividualProfileCard] Error adding goal:', error);
      toast.error('Failed to add goal');
    } finally {
      setAddingGoal(false);
    }
  };

  const handleToggleGoal = async (goal: PlayerGoal) => {
    const nextStatus = goal.status === 'achieved' ? 'in_progress' : 'achieved';
    try {
      const updated = await playerGoalService.updateGoalStatus(goal.id, nextStatus);
      setGoals((prev) => prev.map((g) => (g.id === goal.id ? updated : g)));
    } catch (error) {
      console.error('[IndividualProfileCard] Error updating goal:', error);
      toast.error('Failed to update goal');
    }
  };

  const handleDeleteGoal = async (goalId: string) => {
    try {
      await playerGoalService.deleteGoal(goalId);
      setGoals((prev) => prev.filter((g) => g.id !== goalId));
    } catch (error) {
      console.error('[IndividualProfileCard] Error deleting goal:', error);
      toast.error('Failed to delete goal');
    }
  };

  const positionOptions = useMemo(() => {
    const groups = getPositionGroupsForSport(team.sport);
    if (!groups) return [];
    return groups.units.flatMap((unit) => unit.groups);
  }, [team.sport]);

  const calculatedStats = useMemo(
    () => getCalculatedStats(team.sport, rawStatTotals, team.innings),
    [team.sport, rawStatTotals, team.innings]
  );

  const statProfile = useMemo(
    () => getStatCardProfile(team.sport, player?.position),
    [team.sport, player?.position]
  );

  const rawStatMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const row of rawStatTotals) map.set(row.stat_key, row.stat_value);
    return map;
  }, [rawStatTotals]);

  const hasAnyStat = statProfile.lines.some((line) =>
    line.calculated ? calculatedStats[line.key] !== undefined : (rawStatMap.get(line.key) ?? 0) > 0
  );

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 mb-8 flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary" />
      </div>
    );
  }

  if (!player || !card) return null;

  const activeGoals = goals.filter((g) => g.status !== 'achieved' && g.status !== 'cancelled');
  const achievedGoals = goals.filter((g) => g.status === 'achieved');
  const gamesDecided = record.w + record.l + record.t;
  const eventLabel = getEventLabel(team.sport);

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-8">
      {/* Bio */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-dark">
            {player.first_name} {player.last_name}
            {card.nickname && <span className="text-gray-400 font-normal"> "{card.nickname}"</span>}
          </h3>
          <p className="text-sm text-gray-500">
            {team.sport}
            {player.position && positionOptions.find((p) => p.key === player.position) && (
              <> · {positionOptions.find((p) => p.key === player.position)?.name}</>
            )}
          </p>
        </div>
        {!editingBio && (
          <button
            type="button"
            onClick={() => setEditingBio(true)}
            className="text-gray-400 hover:text-primary p-1.5 rounded hover:bg-gray-50"
            aria-label="Edit profile"
          >
            <Edit2 size={16} />
          </button>
        )}
      </div>

      {editingBio ? (
        <div className="mb-6 space-y-3">
          <div>
            <label htmlFor="profile-nickname" className="block text-xs font-medium text-gray-600 mb-1">Nickname (optional)</label>
            <input
              id="profile-nickname"
              type="text"
              value={nicknameDraft}
              onChange={(e) => setNicknameDraft(e.target.value)}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          {positionOptions.length > 0 && (
            <div>
              <label htmlFor="profile-position" className="block text-xs font-medium text-gray-600 mb-1">Position</label>
              <select
                id="profile-position"
                value={positionDraft}
                onChange={(e) => setPositionDraft(e.target.value)}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">Not set</option>
                {positionOptions.map((pos) => (
                  <option key={pos.key} value={pos.key}>{pos.name}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">Sets which stats show on your card below.</p>
            </div>
          )}
          <div>
            <label htmlFor="profile-bio" className="block text-xs font-medium text-gray-600 mb-1">About me</label>
            <textarea
              id="profile-bio"
              value={bioDraft}
              onChange={(e) => setBioDraft(e.target.value)}
              rows={3}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Favorite shot, what you're working on, anything you'd like to remember later..."
            />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                setEditingBio(false);
                setNicknameDraft(card.nickname || '');
                setBioDraft(card.bio || '');
                setPositionDraft(player.position || '');
              }}
              className="text-sm px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSaveBio}
              disabled={savingBio}
              className="btn-primary text-sm px-3 py-1.5 disabled:opacity-50"
            >
              {savingBio ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      ) : (
        card.bio && <p className="text-sm text-gray-600 mb-6 whitespace-pre-wrap">{card.bio}</p>
      )}

      {/* Record — attendance isn't tracked for a personal account, so no tile for it here. */}
      <div className="bg-secondary-hover rounded-xl p-4 flex items-center gap-3 mb-6 w-fit">
        <div className="p-2 bg-green-100 rounded-lg">
          <Trophy className="h-4 w-4 text-green-600" />
        </div>
        <div>
          <p className="text-lg font-bold text-dark leading-none">
            {gamesDecided === 0 ? `No ${eventLabel.plural.toLowerCase()} yet` : `${record.w}-${record.l}${record.t > 0 ? `-${record.t}` : ''}`}
          </p>
          {gamesDecided > 0 && <p className="text-xs text-gray-500 mt-0.5">{eventLabel.singular} record</p>}
        </div>
      </div>

      {/* Stats — a "trading card" stat line for the player's position, if one is set. */}
      {statProfile.lines.length > 0 && (
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">
            {statProfile.label} Stats
          </h4>
          {hasAnyStat ? (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {statProfile.lines.map((line) => {
                const value = line.calculated ? calculatedStats[line.key] : rawStatMap.get(line.key);
                return (
                  <div key={line.key} className="bg-secondary-hover rounded-xl p-3 text-center">
                    <p className="text-lg font-bold text-dark leading-none">
                      {value === undefined ? '—' : line.calculated ? formatCalculatedStat(value, line.key) : value}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">{line.label}</p>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-gray-500">
              No stats recorded yet — they'll show up here once you log stats on a {eventLabel.singular.toLowerCase()}.
              {!player.position && positionOptions.length > 0 && ' Set a position above to see position-specific stats.'}
            </p>
          )}
        </div>
      )}

      {/* Goals */}
      <div className="mb-2">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Goals</h4>
        {activeGoals.length === 0 && achievedGoals.length === 0 && (
          <p className="text-sm text-gray-500 mb-3">No goals yet — add one below.</p>
        )}
        <ul className="space-y-2 mb-3">
          {[...activeGoals, ...achievedGoals].map((goal) => (
            <li
              key={goal.id}
              className={`flex items-center gap-2 p-2.5 rounded-lg border text-sm ${
                goal.status === 'achieved' ? 'border-green-200 bg-green-50' : 'border-gray-200'
              }`}
            >
              <button
                type="button"
                onClick={() => handleToggleGoal(goal)}
                aria-label={goal.status === 'achieved' ? 'Mark goal not yet achieved' : 'Mark goal achieved'}
                className={`shrink-0 w-5 h-5 rounded-full border flex items-center justify-center ${
                  goal.status === 'achieved' ? 'bg-green-600 border-green-600 text-white' : 'border-gray-300 text-transparent'
                }`}
              >
                <Check size={12} />
              </button>
              <span className={`flex-1 ${goal.status === 'achieved' ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                {goal.goal_text}
              </span>
              <span className="text-xs text-gray-400 capitalize shrink-0">{goal.category}</span>
              <button
                type="button"
                onClick={() => handleDeleteGoal(goal.id)}
                aria-label="Delete goal"
                className="shrink-0 text-gray-300 hover:text-red-500 p-1"
              >
                <Trash2 size={14} />
              </button>
            </li>
          ))}
        </ul>
        <div className="flex gap-2">
          <input
            type="text"
            value={newGoalText}
            onChange={(e) => setNewGoalText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleAddGoal();
            }}
            placeholder="Add a goal..."
            className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          />
          <select
            value={newGoalCategory}
            onChange={(e) => setNewGoalCategory(e.target.value as PlayerGoal['category'])}
            className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            {GOAL_CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={handleAddGoal}
            disabled={addingGoal || !newGoalText.trim()}
            className="btn-primary px-3 py-1.5 flex items-center gap-1 text-sm disabled:opacity-50"
            aria-label="Add goal"
          >
            <Plus size={14} />
          </button>
        </div>
      </div>

      <button
        type="button"
        onClick={() => navigate(`/player-cards/${player.id}`)}
        className="mt-4 inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
      >
        View full profile
        <ExternalLink size={14} />
      </button>
    </div>
  );
};

export default IndividualProfileCard;
